import { Controller, useFormContext } from 'react-hook-form';
import Switch from '@/components/UI/FormElements/Switch';
import { ErrorMessage } from '@hookform/error-message';
import type { FormValues } from '../SingleSignOn';
import type { BaseSsoProviderProps, SsoProviderResponse } from '../SingleSignOn.types';
import { _isNotBlank, _notNil } from '@/littledash';
import { type FC, useEffect, useState } from 'react';
import { ResponseType } from '@/support/LegacyApiService';
import Loading from '@/components/Loading';
import { useLegacyApiHook } from '@/support/Hooks/api/useLegacyApiHook';

const Azure: FC<BaseSsoProviderProps> = ({ onLoadingChange, onSavedEnabledChange }) => {
  const {
    register,
    control,
    formState: { errors },
    setValue,
  } = useFormContext<FormValues>();
  const [hasExistingConfig, setHasExistingConfig] = useState(false);

  const { response, loading } = useLegacyApiHook({
    method: 'GET',
    apiRoute: 'team.sso_providers',
    path: { type: 'azure' },
    options: { onError: { toast: false, throw: false, capture: true, slug: 'azure-sso-load-config' } },
  });

  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(loading);
    }
  }, [loading, onLoadingChange]);

  useEffect(() => {
    if (response?.type === ResponseType.Success) {
      const body = response.body as SsoProviderResponse;
      const data = body?.data;
      if (_notNil(data) && data.type === 'azure') {
        setValue('azure', {
          enabled: data.enabled ?? false,
          tenant: data.settings?.tenant ?? '',
          client_id: data.settings?.client_id ?? '',
          client_secret: data.settings?.client_secret ?? '',
        });
        setHasExistingConfig(_isNotBlank(data.settings?.client_id));
        onSavedEnabledChange?.(data.enabled ?? false);
      }
    }
  }, [response, setValue, onSavedEnabledChange]);

  if (loading) {
    return (
      <div className="ph3 pv4">
        <Loading />
      </div>
    );
  }

  return (
    <div className="ph3">
      <div className="mb3">
        <label>Enable Azure AD SSO</label>
        <Controller
          name="azure.enabled"
          defaultValue={false}
          control={control}
          render={({ field: { value, onChange } }) => <Switch value={value} onChange={onChange} />}
        />
      </div>
      <div className="mb3">
        <label htmlFor="azure.tenant">Tenant ID</label>
        <input
          {...register('azure.tenant', { required: 'This field is required' })}
          type="text"
          style={{ marginBottom: 0 }}
        />
        <ErrorMessage
          errors={errors}
          name="azure.tenant"
          render={({ message }) => <p className="f6 red db pt2">{message}</p>}
        />
      </div>
      <div className="mb3">
        <label htmlFor="azure.client_id">Application (client) ID</label>
        <input
          type="text"
          style={{ marginBottom: 0 }}
          {...register('azure.client_id', { required: 'This field is required' })}
        />
        <ErrorMessage
          errors={errors}
          name="azure.client_id"
          render={({ message }) => <p className="f6 red db pt2">{message}</p>}
        />
      </div>
      <div className="mb3">
        <label htmlFor="azure.client_secret">Secret Value</label>
        <input
          type="password"
          {...register('azure.client_secret', { required: 'This field is required' })}
          placeholder={hasExistingConfig ? '✳︎'.repeat(40) : ''}
          style={{ marginBottom: 0 }}
        />
        <ErrorMessage
          errors={errors}
          name="azure.client_secret"
          render={({ message }) => <p className="f6 red db pt2">{message}</p>}
        />
      </div>
    </div>
  );
};

export default Azure;
